/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.13.0
 */

import { Fragment } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { LinkBlock, Skeleton } from '../../../../../components/common';
import { ELocaleKeys } from '../../../../../modules/internationalization/intConstants';
import {
    makeSelectPageSubscriberIdTranslation,
    makeSelectTranslationCommon,
} from '../../../../../modules/internationalization/intSelectors';
import ButtonServiceUserDenied from '../../../../../modules/role/components/ButtonServiceUserDenied';
import {
    makeSelectGuardComponents,
} from '../../../../../modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '../../../../../modules/wizard/wizardActions';
import { EWizardName } from '../../../../../modules/wizard/wizardConstants';
import { openRoute } from '../../../../../routes/constants/externalRoutes';
import { RoutePath, SUBSCRIBER_ID } from '../../../../../routes/constants/routesConstants';
import { stringFormatParams } from '../../../../../utils/stringFormat';
import { getPageAccountInfoUrl } from '../../../../PageAccountInfo';
import { ECampaignsType } from '../../../../PageCampaign';
import {
    getCampaignsInternalPrefilterUrl,
} from '../../../../PageCampaign/utils/getCampaignsPageUrl';
import { EChangeLockOperationType } from '../../../../WizardChangeLock/types/wizardChangeLockTypes';
import { useSubscriberIdContext } from '../../../context';
import {
    subscriberIdAccessPointsSelector,
    subscriberIdAccountSelector,
    subscriberIdRegionSelector,
    subscriberIdUserTypeSelector,
} from '../../../selectors';
import { ESubscriberIdActions } from '../../../types';
import SubscriberIdError from '../../SubscriberIdError';
import SubscriberIdStatus from '../../SubscriberIdStatus';
import { infoTranslationSelector } from '../selectors';
import { useGetInfoGeneralQuery } from '../store';
import { SubscriberIdInfoGeneralSkeleton } from './SubscriberIdInfoSkeleton';


const SubscriberIdInfoGeneral = () => {

    const { subscriberId, userType } = useSubscriberIdContext();

    const dispatch = useDispatch();

    const { status: tStatus, userTypes: tUserTypes } = useSelector(makeSelectTranslationCommon());
    const { status: translationStatus } = useSelector(makeSelectPageSubscriberIdTranslation());
    const translationTab = useSelector(infoTranslationSelector());

    const componentsFeature = useSelector(makeSelectGuardComponents());

    const accessPoints = useSelector(subscriberIdAccessPointsSelector(subscriberId, userType));
    const account = useSelector(subscriberIdAccountSelector(subscriberId, userType));
    const region = useSelector(subscriberIdRegionSelector(subscriberId, userType));
    const subscriberUserType = useSelector(subscriberIdUserTypeSelector(subscriberId, userType));

    const { data, isSuccess, isFetching, error, refetch } = useGetInfoGeneralQuery(subscriberId);

    const changeLock = (operationType: EChangeLockOperationType) => {
        dispatch(wizardStorageClearStepsAction({
            wizardName: EWizardName.CHANGE_LOCK,
        }));

        const url = RoutePath.wizardChangeLockFromSubscriberId.replace(SUBSCRIBER_ID, subscriberId);

        openRoute(`${url}#${operationType}`);
    };

    const onBlockClick = () => {
        data?.status.isBtnLocked
            ? changeLock(EChangeLockOperationType.UNLOCK)
            : changeLock(EChangeLockOperationType.LOCK);
    };

    if (error || (isSuccess && !data)) return <SubscriberIdError refetch={refetch}/>;
    if (isFetching) return <Skeleton isLoading render={SubscriberIdInfoGeneralSkeleton}/>;
    if (!data) return null;

    const statusPersonalDataHintMessage = data?.statusPersonalData?.description
        || (data?.statusPersonalData?.hintCode
            && (tStatus.codes[data.statusPersonalData.hintCode] || tStatus.defaultMessage))
        || '';

    return (
        <Fragment>
            <SubscriberIdStatus
                {...data.status}
                className="subscriber-id-info__block subscriber-id-info__status"
                title={translationTab.status.title}
                translation={translationStatus}
            >
                {
                    data.actions?.includes(ESubscriberIdActions.lock) && (
                        <ButtonServiceUserDenied onClick={onBlockClick}>
                            {data.status.isBtnLocked
                                ? translationTab.status.btn.unblocked
                                : translationTab.status.btn.blocked}
                        </ButtonServiceUserDenied>
                    )
                }
            </SubscriberIdStatus>

            {
                data.statusPersonalData && (
                    <SubscriberIdStatus
                        {...data.statusPersonalData}
                        className="subscriber-id-info__block subscriber-id-info__status"
                        title={translationTab.statusPersonalData.title}
                        tooltip={statusPersonalDataHintMessage}
                        translation={translationStatus}
                    />
                )
            }

            <div className="subscriber-id-info__block subscriber-id-info__block-account">
                <div className="subscriber-id__title subscriber-id__title_light">{translationTab.accountTitle}</div>
                {
                    account?.isLink
                        ? <LinkBlock url={getPageAccountInfoUrl(componentsFeature, account.id.toString())}>
                            {account.number}
                        </LinkBlock>
                        : <div>{account?.number}</div>
                }
            </div>

            {
                region && (
                    <div className="subscriber-id-info__block">
                        <div className="subscriber-id__title subscriber-id__title_light">
                            {translationTab.regionTitle}
                        </div>
                        <div>{region}</div>
                    </div>
                )
            }

            {
                subscriberUserType && tUserTypes[subscriberUserType] && (
                    <div className="subscriber-id-info__block">
                        <div className="subscriber-id__title subscriber-id__title_light">
                            {translationTab.userTypeTitle}
                        </div>
                        <div>{tUserTypes[subscriberUserType]}</div>
                    </div>
                )
            }

            {
                !!data.campaigns && (
                    <div className="subscriber-id-info__block">
                        <div className="subscriber-id__title subscriber-id__title_light">
                            {translationTab.campaigns.title}
                        </div>
                        <LinkBlock
                            url={getCampaignsInternalPrefilterUrl(ECampaignsType.subscriber, data.msisdn)}
                        >
                            {stringFormatParams(translationTab.campaigns.value, {
                                [ELocaleKeys.number]: data.campaigns,
                            })}
                        </LinkBlock>
                    </div>
                )
            }

            {
                !!accessPoints?.length && (
                    <div className="subscriber-id-info__block">
                        <div className="subscriber-id__title subscriber-id__title_light">
                            {translationTab.accessPoints}
                        </div>
                        {
                            accessPoints.map(accessPoint =>
                                <div
                                    key={accessPoint.apn}>{`${accessPoint.apn}${accessPoint.ip ? `, ${accessPoint.ip}` : ''}`}</div>)
                        }
                    </div>
                )
            }
        </Fragment>
    );
};

export default SubscriberIdInfoGeneral;
